/* ============================================================
   CONVERTIX-IL — depth.js  v2
   Section-to-section transitions.

   Three layers move together as one gesture:
     1. PANELS  each section is a slab travelling in Z, tilting on X
     2. VEIL    a blackout that peaks exactly when a seam crosses centre
     3. RAIN    falling glyphs, drawn above content so they can cover it

   All of it is driven by one value: heat (0..1), the proximity of the
   nearest section boundary to the middle of the viewport.
   ============================================================ */
(function () {
  'use strict';

  var C = window.CONVERTIX_DEPTH || {};
  if (C.enabled === false) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  function num(v, d) { v = parseFloat(v); return isNaN(v) ? d : v; }
  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
  function ease(x) { return x <= 0 ? 0 : x >= 1 ? 1 : x * x * (3 - 2 * x); }

  var mobile = window.matchMedia('(max-width: 900px)').matches;

  var MODE    = C.mode || 'cinematic';
  var TILT    = num(C.tilt, 20)    * (mobile ? 0.5 : 1);
  var DEPTH   = num(C.depth, 760)  * (mobile ? 0.4 : 1);
  var FADE    = clamp(num(C.fade, 0.85), 0, 1);
  var BLUR    = C.blur !== false && !mobile;
  var CURTAIN = clamp(num(C.curtain, 0.88), 0, 1);
  var RAIN    = C.rain !== false;
  var AMB     = clamp(num(C.ambient, 0.09), 0, 1);
  var SPEED   = num(C.speed, 1);
  var SEAM    = C.seam !== false;
  var DECODE  = C.decode !== false;
  var LIME    = C.color || '#C9F03C';
  var BAND    = clamp(num(C.band, 0.38), 0.12, 0.9); // how wide the transition zone is

  if (MODE === 'minimal') { TILT *= 0.35; DEPTH *= 0.3; CURTAIN = 0; BLUR = false; }

  var main = document.getElementById('main') || document.querySelector('main');
  if (!main) return;
  document.body.classList.add('cvx');
  if (MODE === 'snap' && !mobile) document.documentElement.classList.add('cvx-snap');

  /* ---------- slabs ---------- */
  var slabs = [];
  function collect() {
    slabs = [];
    for (var i = 0; i < main.children.length; i++) {
      var el = main.children[i];
      if (el.nodeType !== 1) continue;
      if (/^(SCRIPT|STYLE|LINK|TEMPLATE)$/.test(el.tagName)) continue;
      if (el.offsetHeight < 40) continue;
      slabs.push(el);
    }
  }
  collect();
  if (!slabs.length) return;

  /* ---------- layers ---------- */
  var veil = document.createElement('div');
  veil.className = 'cvx-veil';
  veil.setAttribute('aria-hidden', 'true');
  document.body.appendChild(veil);

  var seamEl = null;
  if (SEAM) {
    seamEl = document.createElement('div');
    seamEl.className = 'cvx-seam';
    seamEl.setAttribute('aria-hidden', 'true');
    document.body.appendChild(seamEl);
  }

  var cv, ctx, cols, drops, cell, trail, W, H, dpr;
  var GLYPHS = (C.glyphs || '01<>/{}[]#*+=\u00B7\u05D0\u05D1\u05D2\u05D3\u05D4\u05D5\u05D6\u05D7\u05D8\u05D9\u05DB\u05DC\u05DE\u05E0\u05E1\u05E2\u05E4\u05E6\u05E7\u05E8\u05E9\u05EA0123456789ACDEILNORSTVX').split('');
  var GL = GLYPHS.length;
  var RGB = (function (hex) {
    var n = parseInt(String(hex).replace('#', ''), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  })(LIME);

  if (RAIN) {
    cv = document.createElement('canvas');
    cv.className = 'cvx-rain';
    cv.setAttribute('aria-hidden', 'true');
    document.body.appendChild(cv);
    ctx = cv.getContext('2d');
    sizeCanvas();
    requestAnimationFrame(function () { cv.classList.add('on'); });
  }

  function sizeCanvas() {
    if (!cv) return;
    dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    W = window.innerWidth;
    H = window.innerHeight;
    cv.width = Math.floor(W * dpr);
    cv.height = Math.floor(H * dpr);
    cv.style.width = W + 'px';
    cv.style.height = H + 'px';
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    cell = mobile ? 20 : 17;
    trail = mobile ? 9 : 17;
    cols = Math.ceil(W / cell) + 1;
    var prev = drops || [];
    drops = new Array(cols);
    for (var i = 0; i < cols; i++) {
      drops[i] = prev[i] !== undefined ? prev[i] : -Math.random() * H * 1.5;
    }
    ctx.font = '500 ' + (cell - 3) + 'px "JetBrains Mono", monospace';
    ctx.textBaseline = 'top';
  }

  /* ---------- state ---------- */
  var heat = 0, smooth = 0, seamY = -1, ticking = false, last = 0;

  function layout() {
    var vh = window.innerHeight, mid = vh * 0.5;
    var topHeat = 0, topY = -1;

    for (var i = 0; i < slabs.length; i++) {
      var el = slabs[i];
      var r = el.getBoundingClientRect();

      if (r.bottom < -vh * 0.4 || r.top > vh * 1.4) {
        if (el.__cvx) { el.style.transform = ''; el.style.opacity = ''; el.style.filter = ''; el.__cvx = false; }
        continue;
      }

      var t = clamp((r.top + r.height / 2 - mid) / vh, -1.15, 1.15);
      var a = Math.abs(t);

      el.style.transform =
        'perspective(1200px) rotateX(' + (-t * TILT).toFixed(2) + 'deg) ' +
        'translateZ(' + (-a * DEPTH).toFixed(1) + 'px)';
      el.style.opacity = (1 - Math.min(a, 1) * FADE).toFixed(3);
      if (BLUR) el.style.filter = a > 0.3 ? 'blur(' + Math.min(a * 7, 6).toFixed(1) + 'px)' : '';
      el.__cvx = true;

      if (i > 0) {
        var h = 1 - Math.abs(r.top - mid) / (vh * BAND);
        if (h > topHeat) { topHeat = h; topY = r.top; }
      }
    }

    heat = ease(clamp(topHeat, 0, 1));
    seamY = topY;

    veil.style.opacity = (Math.pow(heat, 1.7) * CURTAIN).toFixed(3);

    if (seamEl) {
      if (heat > 0.03 && seamY >= 0) {
        seamEl.style.transform = 'translateY(' + seamY.toFixed(1) + 'px) scaleX(' + (0.3 + 0.7 * heat).toFixed(2) + ')';
        seamEl.style.opacity = heat.toFixed(3);
      } else {
        seamEl.style.opacity = '0';
      }
    }
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () { layout(); ticking = false; });
  }

  /* ---------- rain ---------- */
  function frame(ts) {
    requestAnimationFrame(frame);
    if (!ctx || document.hidden) return;
    if (ts - last < 32) return;          // ~30fps
    last = ts;

    smooth += (heat - smooth) * 0.18;
    var h = smooth;
    var alpha = AMB + (0.95 - AMB) * h;  // ambient drizzle -> full downpour at the seam
    var step = cell * SPEED * (0.55 + 2.6 * h);

    ctx.clearRect(0, 0, W, H);

    for (var i = 0; i < cols; i++) {
      var y = drops[i];
      var base = Math.floor(y / cell);
      var x = i * cell;

      for (var k = 0; k < trail; k++) {
        var yy = y - k * cell;
        if (yy < -cell || yy > H) continue;
        var f = 1 - k / trail;
        var av = f * f * alpha;
        if (av < 0.012) continue;
        var g = GLYPHS[Math.abs((i * 31 + (base - k) * 17)) % GL];
        if (k === 0) {
          ctx.fillStyle = 'rgba(238,255,190,' + Math.min(alpha * 1.5, 1).toFixed(3) + ')';
        } else {
          ctx.fillStyle = 'rgba(' + RGB[0] + ',' + RGB[1] + ',' + RGB[2] + ',' + av.toFixed(3) + ')';
        }
        ctx.fillText(g, x, yy);
      }

      drops[i] = y + step;
      if (drops[i] - trail * cell > H) drops[i] = -Math.random() * H * 0.6;
    }
  }

  /* ---------- decoding telemetry labels ---------- */
  function decode() {
    var sel = '.eyebrow b, .vcard__n, .track__tag, .out__k, .plan__n, .hud b, .cform__t, .post__b .mono';
    var els = document.querySelectorAll(sel);
    if (!els.length || !('IntersectionObserver' in window)) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        run(en.target);
        io.unobserve(en.target);
      });
    }, { threshold: 0.5 });

    Array.prototype.forEach.call(els, function (el) {
      var txt = (el.textContent || '').trim();
      if (!txt || txt.length > 44) return;
      io.observe(el);
    });

    function run(el) {
      var final = el.textContent, chars = final.split(''), t0 = null;
      function tick(ts) {
        if (!t0) t0 = ts;
        var done = Math.floor((ts - t0) / 38);
        var outStr = '';
        for (var i = 0; i < chars.length; i++) {
          outStr += chars[i] === ' ' ? ' ' : (i < done ? chars[i] : GLYPHS[(Math.random() * GL) | 0]);
        }
        el.textContent = outStr;
        if (done < chars.length) requestAnimationFrame(tick); else el.textContent = final;
      }
      requestAnimationFrame(tick);
    }
  }

  /* ---------- boot ---------- */
  var rt;
  window.addEventListener('resize', function () {
    clearTimeout(rt);
    rt = setTimeout(function () {
      mobile = window.matchMedia('(max-width: 900px)').matches;
      sizeCanvas();
      collect();
      layout();
    }, 180);
  });
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('load', function () { collect(); layout(); });

  layout();
  if (RAIN) requestAnimationFrame(frame);
  if (DECODE) decode();
})();
